/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2018 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material,
 * and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
 * A Component create a HTML DOM for a given WIP Json format
 *
 *
 */

// Constant hold the mapping of type and HTML element
const components = {
  cite: 'u',
  code: 'code',
  'element-authoredtext': 'p',
  'element-blockfeature': 'blockquote',
  emphasis: 'i',
  inlinecode: 'code',
  span: 'span',
  strong: 'b',
  superscript: 'sup',
  subscript: 'sub',
  superseded: 'strike'
};

class TextRenderer {
  constructor() {
    // Store current itteration index globally (or)
    // it may required to passed parameter to getHierarchy method
    this.currentIndex = 0;
    // it is used to determine the children accomodated inside a parent
    this.parentCharIndex = 0;
    this.content = [];
    // it is used to push child element inside the parent semantics
    this.children = [];
  }

  /**
   * Creates DOM hierarchy for the formatters and mathml content
   * @param formatters Sorted array of formatting to be applied
   * @param charStart current char start position to be applied
   * @param drillDown Deceides Sibling or hierarchial iteration required
   * @returns [DOM element structure] returns Array of DOM element hierarchy
   */
  getHierarchy = (formatters, charStart, drillDown) => {
    const element = {};
    // check have all formatters parsed
    if (this.currentIndex < formatters.length) {
      // get the text value before the char start
      const format = formatters[this.currentIndex];
      if (format.charStart > charStart) {
        const isFirstElement = this.currentIndex === 0;
        let previousTextCharStart = charStart + 1;
        if (isFirstElement && charStart === 0) {
          previousTextCharStart = 0;
        }
        const previousFormat = formatters[this.currentIndex - 1];
        const previousFormatWasInserted = previousFormat && (previousFormat.type === 'mathml' || previousFormat.type === 'image');
        if (previousFormatWasInserted) {
          previousTextCharStart = previousFormat.charEnd;
        }

        const previousTextValue = { type: 'text', charStart: previousTextCharStart, charEnd: format.charStart - 1 };
        this.content.push(previousTextValue);
      }
      if (this.parentCharIndex === 0) {
        this.parentCharIndex = format.charEnd;
      }
      if (this.parentCharIndex < format.charEnd) {
        this.parentCharIndex = format.charEnd;
      }
      element.type = format.type;
      element.charStart = format.charStart;
      element.charEnd = format.type === 'mathml' || 'image' ? format.charEnd : format.charEnd + 1;
      element.mathml = format.mathml;
      element.image = format.image;
      element.children = [];
      if (this.hasChild(formatters, this.currentIndex)) {
        // check hierarchial dom structure exists
        this.currentIndex += 1;
        this.children.unshift(this.getHierarchy(formatters, formatters[this.currentIndex].charStart, false));
      }
      if (drillDown) {
        // Iterate for next index
        this.currentIndex += 1;
        if (element) {
          element.children = this.children;
          this.content.push(element);
          this.children = [];
          this.getHierarchy(formatters, element.charEnd, true);
        }
      }
    }
    return element;
  }

  // Check text semantics have any children (next item char start is less than current char end)
  hasChild = (semantics, index) => (
    (index + 1 < semantics.length && semantics[index + 1].charStart <= semantics[index].charEnd)
    || (index + 1 < semantics.length && this.parentCharIndex >= semantics[index + 1].charStart)
  );

  // club text semantics and mathml content to a single array.  Sort the array using char start
  arrangeContent = (content) => {
    let semantics = [];
    const mathml = [];
    let images = [];
    let definitionLength = content.text && content.text.length;
    if (content.textsemantics) {
      semantics = content.textsemantics;
    }
    if (content.mathml) {
      definitionLength += content.mathml.length + 1;
      // push char end to the mathml content to align with text semantics
      let tempCharStart = '';
      content.mathml.forEach((element) => {
        if (tempCharStart !== element.charAt) {
          mathml.push({
            charStart: element.charAt,
            charEnd: element.charAt,
            mathml: element.xml,
            fallbackimage: element.fallbackimage,
            type: 'mathml'
          });
        } else {
          const firstIndex = mathml.findIndex(math => math.charStart === element.charAt);
          mathml[firstIndex].mathml += element.xml;
        }
        tempCharStart = element.charAt;
      }
      );
    }
    if (content.images) {
      definitionLength += content.images.length + 1;
      // push char end to the images to align with text semantics
      images = content.images.map(element => ({
        charStart: definitionLength,
        charEnd: definitionLength,
        image: `<img class='inlineImageDefinition' src='${element.imagedata.path}' width='${element.imagedata.width}' height='${element.imagedata.height}' alt='${element.imagedata.alttext}'/>`,
        alt: element.imagedata.alttext,
        type: 'image'
      }));
    }
    // merge semantics, mathml, images and sort by char start
    const contents = [...semantics, ...mathml, ...images];
    return contents.sort((element1, element2) => {
      let sortValue = 1;
      if ((element1.charStart <= element2.charStart) && ((element1.charEnd >= element2.charEnd) || (element1.charEnd < element2.charStart))) {
        sortValue = -1;
      }
      if ((element1.charStart > element2.charStart) && (element1.charEnd < element2.charEnd)) {
        sortValue = 1;
      }
      return sortValue;
    });
  }

  /**
   * Public method used to return nested HTML for each elements
   * @element will hold text, textsemantics and mathml
   * @returns {element} returns element content which contains foramated nested html tag
   */
  insertNestedTags = (elementContent, item, element) => {
    let copyElementContent = elementContent;
    const innerTag = components[item.type];
    const tagArray = copyElementContent.match(/<(.*?)>/g) && copyElementContent.match(/<(.*?)>/g).filter(tags => tags.indexOf('/') === -1);
    let addedTagCount = 0;
    if (tagArray && tagArray.length) {
      tagArray.forEach((semantic) => {
        addedTagCount += semantic.length;
      });
    }
    const insertedTextCount = +element.charStart;
    let endIndexToInsert = +item.charEnd + addedTagCount + 1;
    let startIndexToInsert = +item.charStart + addedTagCount;
    if (+element.charStart !== 0) {
      endIndexToInsert = +item.charEnd + addedTagCount - insertedTextCount + 1;
      startIndexToInsert = +item.charStart + addedTagCount - insertedTextCount;
    }
    let tag = `</${innerTag}>`;
    for (let i = 0; i <= 1; i += 1) {
      let indexToInsert = endIndexToInsert;
      if (i === 1) {
        indexToInsert = startIndexToInsert;
        tag = `<${innerTag}>`;
      }
      copyElementContent = [copyElementContent.slice(0, indexToInsert), tag, copyElementContent.slice(indexToInsert)].join('');
    }
    return copyElementContent;
  }

  /**
   * Public method used to return symantic to HTML for each elements
   * @element will hold text, textsemantics and mathml
   * @returns {element} returns element content which contains foramated html tag
   */
  // Get the specific element html content
  getElement = (element, text) => {
    let elementContent = '';
    const tag = components[element.type] || 'span';
    if (element.type === 'mathml') {
      return (`<${tag}>${element.mathml}</${tag}>`);
    }
    if (element.type === 'image') {
      return (`${element.image}`);
    }
    if (element.type === 'text') {
      const startIndex = element.charStart;
      const substring = text.substring(startIndex, +element.charEnd + 1);
      const escapedHtml = this.escapeHtml(substring);
      return escapedHtml;
    }
    if (element.children && element.children.length) {
      elementContent += this.escapeHtml(text.substring(element.charStart, element.children[0].charStart));
      const nonNestedElements = element.children.filter(item => item.charStart === 0 && (item.charEnd === (text.length - 1)));
      const nestedElements = element.children.filter(item => !(item.charStart === 0 && (item.charEnd === (text.length - 1))));

      let tagsElement = '';
      nonNestedElements.forEach((item, key) => {
        const nonNestedTag = components[item.type] || 'span';
        tagsElement = `<${nonNestedTag}>${tagsElement}${(key === 0) ? 'TEXT_CONTENT' : ''}</${nonNestedTag}>`;
      });
      elementContent = tagsElement ? tagsElement.replace('TEXT_CONTENT', text) : text.substring(element.charStart, element.charEnd + 1);
      const duplicateText = elementContent.split(' ').filter((item, pos, self) => !(self.indexOf(item) === pos));
      const nestedContent = elementContent;
      nestedElements.forEach((item) => {
        const nestedText = text.substring(item.charStart, item.charEnd + 1);
        const nestedTag = components[item.type] || 'span';
        if (duplicateText.indexOf(nestedText) >= 0 || item.type === 'mathml') {
          if (item.type !== 'mathml') {
            elementContent = text.replace(nestedContent, elementContent);
          }
          const elementLength = elementContent.replace(new RegExp(/<\/[a-zA-Z]>]*$/), '').length;
          const index = (elementLength - text.length) + item.charStart;
          if (item.type === 'mathml') {
            elementContent = this.handleNestedMathmlSemantics(elementContent, (index >= 0 ? index : 0), nestedText.length - 1, item.mathml);
          } else {
            elementContent = this.handleNestedMathmlSemantics(elementContent, (index >= 0 ? index : 0), nestedText.length, `<${nestedTag}>${nestedText}</${nestedTag}>`);
          }
          elementContent = elementContent.replace(text.slice(0, (text.length - nestedContent.length)), '');
        } else {
          elementContent = elementContent.replace(nestedText, `<${nestedTag}>${nestedText}</${nestedTag}>`);
        }
      });
    } else {
      const cutOffCharacters = this.escapeHtml(text.substring(element.charStart, +element.charEnd + 1));
      elementContent += cutOffCharacters;
    }
    return (`<${tag}>${elementContent}</${tag}>`);
  }
  /**
   * Public method used to convert given WIP JSON symantic to HTML
   * @param content // Glossary definition or title
   * @param index // Character start position
   * @param contentLength // Character length
   * @param replacementContent // Replacing content
   * @returns {string} returns formatted string which study tool interactive support
   */
  handleNestedMathmlSemantics = (content, index, contentLength, replacementContent) => content.substring(0, index) + replacementContent + content.substring(index + contentLength)

  /**
   * Public method used to convert given WIP JSON symantic to HTML
   * @param content WIP data which will hold text, textsemantics and mathml
   * @returns {string} returns formatted string which study tool interactive support
   */
  formatToHtml = (content) => {
    let htmlContent = '';
    const sortedElements = this.arrangeContent(content);
    this.getHierarchy(sortedElements, 0, true);
    const elements = this.content;
    const hasDanglingText = elements.length > 0 && content.text && (elements[elements.length - 1].charEnd <= (content.text.length - 1));
    if (hasDanglingText) {
      let startingIndex = +elements[elements.length - 1].charEnd + 1;
      const hasInsertedElement = elements[elements.length - 1].type === 'mathml';
      if (hasInsertedElement) {
        startingIndex -= 1;
      }
      elements.push(
        {
          type: 'text',
          charStart: startingIndex,
          charEnd: content.text.length
        }
      );
    }
    elements.forEach((element) => {
      htmlContent += this.getElement(element, content.text);
    });
    return htmlContent;
  }

  /**
  * Method to convert html tags to corresponding escape characters
  * This is used to escape all manually placed html tags from Cypress,
  * only those inside textsemantics are used to be displayed as formatted html.
  * @param {*} text
  */
  escapeHtml = (text) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }
}

export default TextRenderer;



// WEBPACK FOOTER //
// ./src/js/util/TextRenderer.js